
/**
 * PostHog privacy route guard.
 *
 * Session replay is restricted to explicitly permitted public pages.
 * Never record authenticated, account, checkout or unknown routes.
 *
 * PostHog initialization and consent persistence are managed by
 * index.html and CookieSettings.jsx.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const CONSENT_KEY = "zy_cookie_prefs_v1";

// Explicit allowlist. Unknown routes are private by default.
const PUBLIC_ROUTES = new Set([
  "/",
  "/pricing",
  "/features",
  "/about",
  "/contact",
  "/blog",
  "/faq",
  "/privacy",
  "/terms",
  "/legal/privacy-policy",
  "/legal/terms-of-service",
  "/legal/cookie-policy",
]);

const PUBLIC_PREFIXES = [
  "/blog/",
];

function normalizePath(pathname) {
  if (!pathname || typeof pathname !== "string") return "";

  const path = pathname.replace(/\/+$/, "");
  return path || "/";
}

function hasAnalyticsConsent() {
  if (typeof window === "undefined" || window.__zyAnalyticsDeniedForPage === true) return false;

  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;

    const prefs = JSON.parse(raw);
    return prefs?.analytics === true;
  } catch {
    return false;
  }
}

function isPublicRecordingRoute(pathname) {
  const path = normalizePath(pathname);

  if (PUBLIC_ROUTES.has(path)) return true;

  return PUBLIC_PREFIXES.some((prefix) =>
    path.startsWith(prefix)
  );
}

function syncPosthogRecording(pathname) {
  if (typeof window === "undefined") return;

  const posthog = window.posthog;

  if (!posthog || !window.__zyPosthogInited) return;

  const mayRecord =
    hasAnalyticsConsent() &&
    isPublicRecordingRoute(pathname);

  try {
    if (mayRecord) {
      posthog.startSessionRecording?.();
    } else {
      posthog.stopSessionRecording?.();
    }
  } catch {
    // Never allow analytics failures to interrupt navigation.
  }
}

export default function usePosthogRouteGuard() {
  const { pathname } = useLocation();

  // Stop recording on every route that is not explicitly public.
  useEffect(() => {
    syncPosthogRecording(pathname);
  }, [pathname]);

  // Respond to consent changes without requiring a page reload.
  useEffect(() => {
    const syncCurrentRoute = () => {
      syncPosthogRecording(window.location.pathname);
    };

    const handleStorageChange = (event) => {
      if (
        event.key === CONSENT_KEY ||
        event.key === null
      ) {
        syncCurrentRoute();
      }
    };

    window.addEventListener(
      "storage",
      handleStorageChange
    );

    window.addEventListener(
      "zy:analytics-consent-changed",
      syncCurrentRoute
    );

    return () => {
      window.removeEventListener(
        "storage",
        handleStorageChange
      );

      window.removeEventListener(
        "zy:analytics-consent-changed",
        syncCurrentRoute
      );
    };
  }, []);
}
